import { useWorkspaceText } from './interface-copy';
// The meetings page (BUILD 1 WP-4): Record in Chat next to Import, capturing
// rows that show "Recording… 12:34" or "Saving…", and the import dialog. There
// is no spoken-language choice: engines detect it. When this server cannot
// transcribe audio, the import takes transcripts only. Copy:
// ui/i18n/ns/meetings-record.ts.
import { useCallback, useEffect, useRef, useState } from 'react';
import { CompanyRequestError } from '@/company/api';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import meetingsRecordCopy from '@/i18n/ns/meetings-record';
import { directUploadFile } from './direct-upload';
import { uploadRefusalMessage } from './file-upload';
import { MAX_UPLOAD_BYTES, UPLOAD_RULE, uploadMime, workspaceApi } from './api';
import { useWorkspace } from './context';
import { MeetingAttendeePicker, attendeeInputs } from './meeting-attendees';
import { announceMeetingUpdated, subscribeMeetingUpdates } from './meeting-events';
import { makeMeetingPrivate, meetingShareItem } from './meeting-sharing';
import { audienceLabel } from './sharing-api';
import { MeetingRecordControl } from './meeting-record-control';
import { formatClock } from './meeting-record-parts';
import { recorderElapsedMs, useMeetingRecorder } from './meeting-recorder';
import { useMeetingStageLabel } from './meeting-status';
import { meetingsApi } from './meetings-api';
import { WorkspaceIcon } from './icons';
import { MeetingDialog, MeetingDialogTitle } from './meeting-dialog';
import { dateLabel, EmptyState, ErrorState, LoadingState, PageHeading } from './primitives';
import type { CompanyMeeting, CompanyMember, MeetingCapabilities } from './types';

const TRANSCRIPT_ACCEPT = '.txt,.md,.vtt,.srt,.json';
const MEETING_ACCEPT = `${TRANSCRIPT_ACCEPT},.m4a,.mp3,.wav,.webm,.ogg,.mp4`;
const TRANSCRIPT_TYPES = new Set([
  'text/plain',
  'text/markdown',
  'text/vtt',
  'application/x-subrip',
  'application/json',
]);

/** The clock of the meeting this device records: the only row that follows it. */
function RecordingRowClock() {
  const copy = useNamespaceCopy(meetingsRecordCopy);
  const elapsed = useMeetingRecorder(recorderElapsedMs);
  return <>{formatCopy(copy.rowRecording, { time: formatClock(elapsed) })}</>;
}

/**
 * "Recording… 12:34" or "Saving…" for a meeting the recorder still owns; else
 * the stage in the interface language. Exported for tests.
 */
export function MeetingRowStatus({ meeting }: { meeting: CompanyMeeting }) {
  const copy = useNamespaceCopy(meetingsRecordCopy);
  const stageLabel = useMeetingStageLabel();
  const recording = useMeetingRecorder((state) => state.meetingId === meeting.id);
  if (meeting.status === 'capturing') {
    if (recording) return <RecordingRowClock />;
    const time = Date.now() - new Date(meeting.createdAt).getTime();
    return <>{formatCopy(copy.rowRecording, { time: formatClock(time) })}</>;
  }
  if (meeting.stage === 'sealing' && meeting.status === 'processing') return <>{copy.rowSaving}</>;
  return <>{stageLabel(meeting)}</>;
}

function MeetingByline({ meeting }: { meeting: CompanyMeeting }) {
  const ws = useWorkspace();
  const { text: uiText, locale } = useWorkspaceText();
  const creator = meeting.createdBy === ws.user?.id ? uiText('You') : meeting.createdByName;
  return (
    <>
      {creator && <>{creator} · </>}
      <span className="ws-audience-chip">
        {audienceLabel(meetingShareItem(meeting), ws.companyName, locale)}
      </span>
    </>
  );
}

async function loadMeetings(shown: number) {
  const result: CompanyMeeting[] = [];
  const seen = new Set<string>();
  let offset = 0;
  for (;;) {
    const page = await workspaceApi.meetingsPage(offset);
    for (const meeting of page.meetings)
      if (!seen.has(meeting.id)) {
        seen.add(meeting.id);
        result.push(meeting);
      }
    if (
      page.hasMore !== true ||
      !page.meetings.length ||
      typeof page.nextOffset !== 'number' ||
      result.length > shown
    )
      return { meetings: result, hasMore: page.hasMore === true };
    offset = page.nextOffset;
  }
}

function ImportDialog({
  file,
  members,
  onClose,
  onImported,
}: {
  file: File;
  members: CompanyMember[];
  onClose: () => void;
  onImported: (meeting: CompanyMeeting) => void;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const copy = useNamespaceCopy(meetingsRecordCopy);
  const [title, setTitle] = useState(file.name.replace(/\.[^.]+$/u, ''));
  const [selected, setSelected] = useState<string[]>(() =>
    ws.user?.id && members.some((member) => member.id === ws.user?.id) ? [ws.user.id] : [],
  );
  const [guests, setGuests] = useState('');
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [visibility, setVisibility] = useState<'company' | 'private'>('company');

  async function create() {
    const mimeType = uploadMime(file.name);
    if (saving || !mimeType) return;
    setSaving(true);
    try {
      const uploaded = await directUploadFile({
        file,
        mimeType,
        includeInKnowledge: false,
        purpose: 'meeting',
        onProgress: setProgress,
      });
      const meeting = await meetingsApi.create({
        fileId: uploaded.file.id,
        ...(title.trim() ? { title: title.trim() } : {}),
        captureMethod: 'upload',
        participants: attendeeInputs(selected, guests),
        visibility,
      });
      onImported(meeting);
    } catch (failure) {
      ws.notify(
        failure instanceof CompanyRequestError
          ? uploadRefusalMessage(file.name, failure)
          : copy.importFailed,
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <MeetingDialog
      label={copy.importDialogTitle}
      keepOnOutsidePress
      closable={!saving}
      onClose={onClose}
    >
      <header>
        <div>
          <MeetingDialogTitle>{copy.importDialogTitle}</MeetingDialogTitle>
          <p>{file.name}</p>
        </div>
        <button
          className="ws-icon-button"
          aria-label={copy.close}
          disabled={saving}
          onClick={onClose}
        >
          <WorkspaceIcon name="close" />
        </button>
      </header>
      <div className="ws-meeting-dialog-body">
        <label>
          {copy.titleLabel}
          <input value={title} onChange={(event) => setTitle(event.target.value)} />
        </label>
        <MeetingAttendeePicker
          members={members}
          currentUserId={ws.user?.id}
          companyName={ws.companyName}
          selected={selected}
          onSelected={setSelected}
          guests={guests}
          onGuests={setGuests}
        />
        <label>
          {uiText('Who can see this')}
          <select
            value={visibility}
            onChange={(event) => setVisibility(event.target.value as typeof visibility)}
          >
            <option value="company">
              {uiRich('Everyone at {value1}', { value1: ws.companyName })}
            </option>
            <option value="private">{uiText('Only me')}</option>
          </select>
        </label>
        <p className="ws-page-note">
          {visibility === 'company'
            ? uiText('Everyone at {value1} can find it and ask about it.', {
                value1: ws.companyName,
              })
            : uiText('Only you can open and search this meeting. You can share it later.')}
        </p>
        <p className="ws-page-note">{copy.importRetainedNote}</p>
        {saving && (
          <progress
            className="ws-upload-progress"
            max={1}
            value={progress}
            aria-label={copy.uploadProgress}
          />
        )}
      </div>
      <footer>
        <button className="ws-button" disabled={saving} onClick={onClose}>
          {copy.cancel}
        </button>
        <button className="ws-button primary" disabled={saving} onClick={() => void create()}>
          {saving
            ? formatCopy(copy.uploadingPercent, { percent: Math.round(progress * 100) })
            : copy.importMeeting}
        </button>
      </footer>
    </MeetingDialog>
  );
}

export function MeetingsPage() {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const copy = useNamespaceCopy(meetingsRecordCopy);
  const picker = useRef<HTMLInputElement>(null);
  const [meetings, setMeetings] = useState<CompanyMeeting[]>([]);
  const [shown, setShown] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [capabilities, setCapabilities] = useState<MeetingCapabilities | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const load = useCallback(async () => {
    try {
      const [nextMeetings, nextMembers] = await Promise.all([
        loadMeetings(shown),
        workspaceApi.members(),
      ]);
      setMeetings(nextMeetings.meetings);
      setHasMore(nextMeetings.hasMore);
      setMembers(nextMembers);
      setError('');
    } catch {
      setError(copy.loadFailed);
    } finally {
      setLoading(false);
    }
  }, [copy.loadFailed, shown]);
  useEffect(() => void load(), [load]);
  useEffect(() => subscribeMeetingUpdates(() => void load()), [load]);
  useEffect(() => {
    let active = true;
    void meetingsApi
      .capabilities()
      .then((value) => active && setCapabilities(value))
      .catch(() => null);
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    if (!meetings.some((meeting) => ['processing', 'capturing'].includes(meeting.status))) return;
    const timer = window.setInterval(() => void load(), 4000);
    return () => window.clearInterval(timer);
  }, [meetings, load]);

  const audioImport = capabilities?.import?.audio ?? true;
  function choose(next: File) {
    const mimeType = uploadMime(next.name);
    if (!mimeType || next.size > MAX_UPLOAD_BYTES) {
      ws.notify(UPLOAD_RULE);
      return;
    }
    if (!audioImport && !TRANSCRIPT_TYPES.has(mimeType)) {
      ws.notify(copy.importUnavailable);
      return;
    }
    setFile(next);
  }
  const openMeeting = (id: string) => ws.openCanvas({ kind: 'meeting', id });
  function announceAdded(meeting: CompanyMeeting) {
    if (meeting.visibility !== 'company') {
      ws.notify(uiText('Added. Only you can see it. Transcription is running in the background.'));
      return;
    }
    ws.notify(uiText('Visible to everyone at {value1}', { value1: ws.companyName }), {
      label: uiText('Make private'),
      run: async () => {
        try {
          await makeMeetingPrivate(meeting);
          await load();
          announceMeetingUpdated();
          ws.notify(uiText('Made private. Only you can see this meeting.'));
        } catch (failure) {
          ws.notify(
            failure instanceof Error ? failure.message : uiText('Could not make it private.'),
          );
        }
      },
    });
  }
  const importButton = (label: string) => (
    <button className="ws-button" onClick={() => picker.current?.click()}>
      <WorkspaceIcon name="upload" size={15} /> {label}
    </button>
  );

  return (
    <div className="ws-meetings-page">
      <PageHeading
        eyebrow={formatCopy(copy.pageEyebrow, { company: ws.companyName })}
        title={copy.pageTitle}
        description={copy.pageDescription}
      >
        <input
          ref={picker}
          className="ws-sr-only"
          type="file"
          accept={audioImport ? MEETING_ACCEPT : TRANSCRIPT_ACCEPT}
          onChange={(event) => {
            const selected = event.target.files?.[0];
            if (selected) choose(selected);
            event.target.value = '';
          }}
        />
        <MeetingRecordControl
          members={members}
          currentUserId={ws.user?.id}
          companyName={ws.companyName}
          onRecording={openMeeting}
        />
        {importButton(copy.importMeeting)}
      </PageHeading>
      {!audioImport && <p className="ws-page-note">{copy.importUnavailable}</p>}
      {error ? (
        <ErrorState message={error} onRetry={() => void load()} />
      ) : loading ? (
        <LoadingState label={copy.loadingMeetings} />
      ) : !meetings.length ? (
        <EmptyState icon="meetings" title={copy.emptyTitle} description={copy.emptyDescription}>
          {importButton(copy.importFirst)}
        </EmptyState>
      ) : (
        <div className="ws-meeting-list">
          {meetings.map((meeting) => (
            <button
              className="ws-record"
              key={meeting.id}
              data-status={meeting.status}
              onClick={() => openMeeting(meeting.id)}
            >
              <span className="ws-record-symbol">
                {meeting.status === 'capturing' ? (
                  <span className="ws-record-dot live" aria-hidden="true" />
                ) : (
                  <WorkspaceIcon name="meetings" size={21} />
                )}
              </span>
              <span className="ws-record-copy">
                <strong>{meeting.title}</strong>
                <small>
                  <MeetingRowStatus meeting={meeting} /> · <MeetingByline meeting={meeting} />
                </small>
              </span>
              <span className="ws-record-date">{dateLabel(meeting.createdAt, uiText.locale)}</span>
              <WorkspaceIcon name="chevron" size={16} />
            </button>
          ))}
          {hasMore && (
            <button className="ws-button ws-meeting-more" onClick={() => setShown(meetings.length)}>
              {uiText('Show more meetings')}
            </button>
          )}
        </div>
      )}
      {file && (
        <ImportDialog
          file={file}
          members={members}
          onClose={() => setFile(null)}
          onImported={(meeting) => {
            setFile(null);
            void load();
            openMeeting(meeting.id);
            announceAdded(meeting);
          }}
        />
      )}
    </div>
  );
}
